import { useCallback } from "react";
import { FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import { isImageFile, openNewSpotIncentiveEditor } from "@/other/utils/utils.jsx";
import { prepareFileForSpotIncentive } from "@/react/components/entityViews/files/utils/spotIncentiveFileUtils";
import { toast } from "sonner";
import type { ISwivuProject } from "@/types/SwivuProject";
import type { EntityRecord } from "@/types/entity";

interface UseFileSpotIncentiveProps {
  entityRecord: EntityRecord;
  project: ISwivuProject | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editorHandler: any;
  onSuccess?: () => void;
}

/**
 * Custom hook to handle spot incentive operations for files
 * Provides logic for checking if a file is eligible for spot incentive
 * and handler for opening the spot incentive editor
 */
export const useFileSpotIncentive = ({
  entityRecord,
  project,
  editorHandler,
  onSuccess
}: UseFileSpotIncentiveProps) => {
  /**
   * Check if "Adjust Points" should be shown for a file
   */
  const shouldShowAdjustPoints = useCallback(
    (file: FormattedFile | null) => {
      if (!file) return false;

      const isImage = isImageFile(file);

      const isTaskRelated = file.isCCImage
        ? entityRecord?.isTask === true
        : Boolean(file.record?.taskId);

      return isImage && isTaskRelated;
    },
    [entityRecord]
  );

  /**
   * Handler for adjusting points on a file
   */
  const handleAdjustPoints = useCallback(
    async (file: FormattedFile) => {
      if (!file || !project) return;

      try {
        const fileData = await prepareFileForSpotIncentive(file);

        if (!fileData) {
          toast.error("Unable to prepare file for spot incentive");
          return;
        }

        let task = null;
        if (file.isCCImage) {
          task = entityRecord?.isTask ? entityRecord : null;
        } else {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          task = file.record?.taskId ? (project as any).extraTaskStore.getById(file.record.taskId) : null;
        }

        openNewSpotIncentiveEditor({
          project,
          editorHandler,
          otherParams: {
            selectedTask: task,
            initialFileData: fileData
          }
        });

        onSuccess?.();
      } catch (error) {
        console.error("Error creating spot incentive:", error);
        toast.error("Failed to create spot incentive");
      }
    },
    [project, editorHandler, entityRecord, onSuccess]
  );

  return {
    shouldShowAdjustPoints,
    handleAdjustPoints
  };
};
